import { useEffect } from 'react';
import { usePresentationStore } from '../../stores/presentation-store';
import { useSongStore } from '../../stores/song-store';
import { useFontStore } from '../../stores/font-store';
import { refreshImageCloudMap } from '../../stores/cast-store';

/**
 * Listens for 'proclaim:sync' CustomEvents dispatched by the sync service
 * and refetches all domain stores. Mount once at App root.
 *
 * Reads current open record via getState() at event time — avoids re-registering
 * the listener every time the user navigates between presentations/songs.
 *
 * Triggered by:
 * - Electron runSync() when pulled>0 || pushed>0
 * - Electron SSE on (re)connect (catch-up after dropout)
 * - Web SSE on 'data: sync' from server
 * - Web SSE on (re)connect after network dropout
 * - Web 5-min periodic fallback
 */
export function useSyncRefresh() {
  useEffect(() => {
    const handler = () => {
      usePresentationStore.getState().fetchPresentations(true);
      useSongStore.getState().fetchSongs(true);
      useFontStore.getState().fetchFonts();
      refreshImageCloudMap().catch(() => {/* non-fatal */});

      const currentPresentation = usePresentationStore.getState().currentPresentation;
      if (currentPresentation?.id) {
        usePresentationStore.getState().fetchPresentation(currentPresentation.id, true);
      }

      const currentSong = useSongStore.getState().currentSong;
      if (currentSong?.id) {
        useSongStore.getState().fetchSong(currentSong.id, true);
      }
    };

    window.addEventListener('proclaim:sync', handler);
    return () => window.removeEventListener('proclaim:sync', handler);
  }, []);
}
